@file:Suppress("TooGenericExceptionCaught")
package com.klantenstop.web.pages.admin.curatedcities.manage

import com.klantenstop.web.data.models.dto.requests.address.ValidateCityDto
import com.klantenstop.web.data.models.dto.requests.curatedcity.InsertCuratedCity
import com.klantenstop.web.data.models.dto.requests.curatedcity.UpdateCuratedCity
import com.klantenstop.web.data.repos.AddressRepo
import com.klantenstop.web.data.repos.CuratedCityRepo
import com.materialkobweb.utils.ViewModel
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ManageCuratedCityViewModel(
    private val curatedCityId: String?,
    private val curatedCityRepo: CuratedCityRepo = CuratedCityRepo,
    private val addressRepo: AddressRepo = AddressRepo
) : ViewModel(), ManageCuratedCityContract {

    private val _state = MutableStateFlow(ManageCuratedCityContract.State())
    override val state: StateFlow<ManageCuratedCityContract.State> = _state.asStateFlow()

    private val _effect = MutableSharedFlow<ManageCuratedCityContract.Effect>()
    override val effect: SharedFlow<ManageCuratedCityContract.Effect> = _effect.asSharedFlow()

    init {
        curatedCityId?.let {
            sendIntent(ManageCuratedCityContract.Intent.LoadCuratedCity(it))
        }
    }

    override fun sendIntent(intent: ManageCuratedCityContract.Intent) = coroutineScope.launch {
        when (intent) {
            is ManageCuratedCityContract.Intent.LoadCuratedCity -> loadCuratedCity(intent.curatedCityId)
            is ManageCuratedCityContract.Intent.UpdateName -> updateState { it.copy(name = intent.name) }
            is ManageCuratedCityContract.Intent.UpdateProvince -> updateState { it.copy(province = intent.province) }
            is ManageCuratedCityContract.Intent.UpdateLatitude -> updateState { it.copy(latitude = intent.latitude, geocodeState = null) }
            is ManageCuratedCityContract.Intent.UpdateLongitude -> updateState { it.copy(longitude = intent.longitude, geocodeState = null) }
            is ManageCuratedCityContract.Intent.UpdateIsFeatured -> updateState { it.copy(isFeatured = intent.isFeatured) }
            is ManageCuratedCityContract.Intent.GeocodeCity -> geocodeCity()
            is ManageCuratedCityContract.Intent.SaveCuratedCity -> saveCuratedCity()
            is ManageCuratedCityContract.Intent.NavigateBack -> emitEffect(ManageCuratedCityContract.Effect.NavigateBack)
        }
    }

    override fun emitEffect(effect: ManageCuratedCityContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (ManageCuratedCityContract.State) -> ManageCuratedCityContract.State) {
        _state.update(block)
    }

    private suspend fun loadCuratedCity(id: String) {
        updateState { it.copy(isLoading = true) }
        try {
            val curatedCity = curatedCityRepo.getCuratedCityById(id)
            updateState {
                it.copy(
                    curatedCity = curatedCity,
                    name = curatedCity.name,
                    province = curatedCity.province,
                    latitude = curatedCity.latitude.toString(),
                    longitude = curatedCity.longitude.toString(),
                    isFeatured = curatedCity.isFeatured,
                    isLoading = false
                )
            }
        } catch (e: Exception) {
            updateState { it.copy(isLoading = false) }
            emitEffect(ManageCuratedCityContract.Effect.ShowError(e.message ?: "Fout bij het ophalen van stad"))
            emitEffect(ManageCuratedCityContract.Effect.NavigateBack)
        }
    }

    private suspend fun geocodeCity() {
        val currentState = state.value
        if (currentState.name.isBlank()) {
            emitEffect(ManageCuratedCityContract.Effect.ShowError("Vul een naam in voordat je de locatie opzoekt."))
            return
        }

        updateState { it.copy(isGeocoding = true, geocodeState = null) }
        try {
            // "België" (not the stored "BE" country code) is sent here because Nominatim's structured
            // `country` search parameter expects a country name, not an ISO code. The persisted
            // CuratedCity.country field (always "BE", hidden from the UI per spec) is unaffected.
            val validationResponse = addressRepo.validateCity(
                ValidateCityDto(city = currentState.name, country = "België", province = currentState.province?.slug)
            )

            if (validationResponse?.valid == true && validationResponse.latitude != null && validationResponse.longitude != null) {
                updateState {
                    it.copy(
                        isGeocoding = false,
                        geocodeState = ManageCuratedCityContract.GeocodeState.Success,
                        latitude = validationResponse.latitude.toString(),
                        longitude = validationResponse.longitude.toString()
                    )
                }
            } else {
                updateState { it.copy(isGeocoding = false, geocodeState = ManageCuratedCityContract.GeocodeState.Error) }
                emitEffect(
                    ManageCuratedCityContract.Effect.ShowError(
                        validationResponse?.message ?: "Locatie kon niet worden gevonden. Vul de coördinaten handmatig in."
                    )
                )
            }
        } catch (e: Exception) {
            updateState { it.copy(isGeocoding = false, geocodeState = ManageCuratedCityContract.GeocodeState.Error) }
            emitEffect(ManageCuratedCityContract.Effect.ShowError(e.message ?: "Fout bij het opzoeken van de locatie"))
        }
    }

    private suspend fun saveCuratedCity() {
        val currentState = state.value
        if (!currentState.isValid) {
            emitEffect(ManageCuratedCityContract.Effect.ShowError("Fout bij het opslaan van stad, invoer is ongeldig."))
            return
        }

        val province = currentState.province ?: return
        val latitude = currentState.latitude.toDoubleOrNull() ?: return
        val longitude = currentState.longitude.toDoubleOrNull() ?: return

        updateState { it.copy(isSaving = true) }
        try {
            if (curatedCityId == null) {
                curatedCityRepo.insertCuratedCity(
                    InsertCuratedCity(
                        name = currentState.name,
                        province = province.slug,
                        latitude = latitude,
                        longitude = longitude,
                        isFeatured = currentState.isFeatured
                    )
                )
                emitEffect(ManageCuratedCityContract.Effect.ShowSuccess("Stad aangemaakt"))
            } else {
                curatedCityRepo.updateCuratedCity(
                    curatedCityId,
                    UpdateCuratedCity(
                        name = currentState.name,
                        province = province.slug,
                        latitude = latitude,
                        longitude = longitude,
                        isFeatured = currentState.isFeatured
                    )
                )
                emitEffect(ManageCuratedCityContract.Effect.ShowSuccess("Stad bijgewerkt"))
            }
            emitEffect(ManageCuratedCityContract.Effect.NavigateBack)
        } catch (e: Exception) {
            emitEffect(ManageCuratedCityContract.Effect.ShowError(e.message ?: "Fout bij het opslaan van stad"))
        } finally {
            updateState { it.copy(isSaving = false) }
        }
    }
}
